import {
  Field,
  FieldGrid,
  FormBlock,
  Input,
  ReadOnlyField,
  SavedIndicator,
  Select,
  Textarea,
  formatCurrency,
  titleCase,
} from '@westhetics/ui'
import { useId, useState, type ReactNode } from 'react'
import { usePractitioners } from '@/features/practitioners/api'
import { brDateToIso, isoToBrDate, maskDate } from '@/lib/dates'
import type { TreatmentPlan, TreatmentPlanInput } from './api'
import { IMAGE_CONSENT_LABELS, isValidTime } from './labels'
import type { Autosave } from './use-autosave'

/**
 * A aba Informações gerais (RN-PLA-05, RN-PLA-06): o que foi copiado não se edita (paciente,
 * procedimentos, valor, uso de imagem); idade só sem nascimento no cadastro; profissional só no
 * plano manual com o atendimento em aberto; data, horário e observações gravam sozinhos
 * (RN-PLA-24), e "salvo há 2 minutos" fica no rodapé do formulário (T35).
 */

const MSG_DATE = 'Use o formato dd/mm/aaaa.'
const MSG_TIME = 'Use o formato HH:mm, como 14:30.'
const MSG_TIME_WITHOUT_DATE = 'Informe a data do procedimento junto com o horário.'
const MSG_AGE = 'Informe a idade entre 0 e 120 anos.'
const MSG_NOTES = 'As observações podem ter no máximo 10.000 caracteres.'

export function GeneralTab({
  plan,
  autosave,
  readOnly,
  errors = {},
  children,
}: {
  plan: TreatmentPlan
  autosave: Autosave<TreatmentPlanInput>
  readOnly: boolean
  /** O que a API recusou (422 do `PUT`), por campo. */
  errors?: Record<string, string>
  /** Os cartões de ação (encerrar, arquivar, prova, restaurar), depois do formulário. */
  children?: ReactNode
}) {
  const id = useId()
  const [date, setDate] = useState(isoToBrDate(plan.procedureDate))
  const [time, setTime] = useState(plan.procedureTime ?? '')
  const [age, setAge] = useState(plan.ageAtProcedure === null ? '' : String(plan.ageAtProcedure))
  const [notes, setNotes] = useState(plan.notes)
  const [practitionerId, setPractitionerId] = useState(plan.practitioner.id)
  const [local, setLocal] = useState<Record<string, string | undefined>>({})
  const practitioners = usePractitioners({ filter: 'active', perPage: 50 })
  const canEditAge = !readOnly && plan.patient.birthDate === null
  const canEditPractitioner = !readOnly && plan.isPractitionerEditable
  const error = (key: string) => local[key] ?? errors[key] ?? null

  function commitDate(nextDate = date, nextTime = time) {
    const trimmed = nextDate.trim()
    if (trimmed === '') {
      setLocal((c) => ({
        ...c,
        procedureDate: undefined,
        procedureTime: nextTime.trim() ? MSG_TIME_WITHOUT_DATE : undefined,
      }))
      if (plan.procedureDate !== null) autosave.change({ procedureDate: null, procedureTime: null })
      return
    }
    const iso = brDateToIso(trimmed)
    if (!iso) {
      setLocal((c) => ({ ...c, procedureDate: MSG_DATE }))
      return
    }
    setLocal((c) => ({ ...c, procedureDate: undefined }))
    const patch: TreatmentPlanInput = {}
    if (iso !== plan.procedureDate) patch.procedureDate = iso
    const t = nextTime.trim()
    if (t === '') {
      if (plan.procedureTime !== null) patch.procedureTime = null
      setLocal((c) => ({ ...c, procedureTime: undefined }))
    } else if (isValidTime(t)) {
      if (t !== plan.procedureTime) patch.procedureTime = t
      setLocal((c) => ({ ...c, procedureTime: undefined }))
    } else {
      setLocal((c) => ({ ...c, procedureTime: MSG_TIME }))
    }
    if (Object.keys(patch).length > 0) autosave.change(patch)
  }

  function commitAge() {
    const value = age.trim()
    const parsed = Number(value)
    if (value === '' || !Number.isInteger(parsed) || parsed < 0 || parsed > 120) {
      setLocal((c) => ({ ...c, ageAtProcedure: MSG_AGE }))
      return
    }
    setLocal((c) => ({ ...c, ageAtProcedure: undefined }))
    if (parsed !== plan.ageAtProcedure) autosave.change({ ageAtProcedure: parsed })
  }

  function changeNotes(value: string) {
    setNotes(value)
    if (value.length > 10_000) {
      setLocal((c) => ({ ...c, notes: MSG_NOTES }))
      return
    }
    setLocal((c) => ({ ...c, notes: undefined }))
    autosave.change({ notes: value })
  }

  const practitionerLine = [
    titleCase(plan.practitioner.name),
    plan.practitioner.profession,
    [plan.practitioner.council, plan.practitioner.registrationNumber].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="flex flex-col gap-4">
      <FormBlock
        title="Paciente e atendimento"
        note="Copiados quando o plano nasceu; vão para o PDF e não mudam aqui."
      >
        <FieldGrid>
          <ReadOnlyField label="Paciente" value={titleCase(plan.patient.name)} />
          {canEditAge ? (
            <Field
              id={`${id}-age`}
              label="Idade na data do procedimento"
              error={error('ageAtProcedure')}
              hint="Sem nascimento no cadastro, a idade é informada aqui."
            >
              <Input
                inputMode="numeric"
                value={age}
                onChange={(e) => setAge(e.target.value.replace(/\D/g, '').slice(0, 3))}
                onBlur={commitAge}
              />
            </Field>
          ) : (
            <ReadOnlyField
              label="Idade"
              value={plan.ageAtProcedure === null ? '—' : `${plan.ageAtProcedure} anos`}
              note={plan.isAgeEnteredManually ? '· informada' : '· pelo nascimento'}
            />
          )}
          <ReadOnlyField
            label="Uso de imagem"
            value={IMAGE_CONSENT_LABELS[plan.imageConsent]}
            note="· do atendimento"
          />
          <ReadOnlyField label="Valor total" value={formatCurrency(plan.totalAmountCents / 100)} />
          <ReadOnlyField
            className="col-span-full"
            label="Procedimentos contratados"
            note="· vem do contrato"
            value={plan.procedures
              .map((p) => `${p.name} · ${formatCurrency(p.amountCents / 100)}`)
              .join(' · ')}
          />
        </FieldGrid>
      </FormBlock>

      <FormBlock
        title="Profissional, data e observações"
        note="A data alimenta a agenda e o PDF; as observações entram no PDF do plano."
      >
        <FieldGrid>
          {canEditPractitioner ? (
            <Field
              id={`${id}-practitioner`}
              label="Profissional responsável"
              error={error('practitionerId')}
              hint="Editável enquanto o atendimento não tem documentos."
            >
              <Select
                value={practitionerId}
                disabled={practitioners.isPending}
                onChange={(e) => {
                  setPractitionerId(e.target.value)
                  if (e.target.value && e.target.value !== plan.practitioner.id) {
                    autosave.change({ practitionerId: e.target.value })
                    autosave.flush()
                  }
                }}
              >
                {(practitioners.data?.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {titleCase(p.name)}
                  </option>
                ))}
                {!practitioners.data?.data.some((p) => p.id === plan.practitioner.id) ? (
                  <option value={plan.practitioner.id}>{titleCase(plan.practitioner.name)}</option>
                ) : null}
              </Select>
            </Field>
          ) : (
            <ReadOnlyField
              label="Profissional responsável"
              value={practitionerLine}
              note={readOnly ? null : '· vem do contrato'}
            />
          )}
          <Field
            id={`${id}-date`}
            label="Data do procedimento"
            optional
            error={error('procedureDate')}
          >
            <Input
              inputMode="numeric"
              placeholder="dd/mm/aaaa"
              value={date}
              readOnly={readOnly}
              onChange={(e) => setDate(maskDate(e.target.value))}
              onBlur={() => commitDate()}
            />
          </Field>
          <Field id={`${id}-time`} label="Horário" optional error={error('procedureTime')}>
            <Input
              inputMode="numeric"
              placeholder="HH:mm"
              value={time}
              readOnly={readOnly}
              onChange={(e) => setTime(e.target.value.replace(/[^\d:]/g, '').slice(0, 5))}
              onBlur={() => commitDate(date, time)}
            />
          </Field>
          <Field
            id={`${id}-notes`}
            className="col-span-full"
            label="Observações"
            optional
            error={error('notes')}
            meta={`${notes.length}/10.000`}
          >
            <Textarea
              rows={4}
              value={notes}
              readOnly={readOnly}
              onChange={(e) => changeNotes(e.target.value)}
              onBlur={() => autosave.flush()}
            />
          </Field>
        </FieldGrid>
        {!readOnly ? (
          <div
            role="group"
            aria-label="Estado do formulário"
            className="border-hairline mt-5 border-t pt-4"
          >
            <SavedIndicator
              savedAt={new Date(plan.updatedAt)}
              saving={autosave.status === 'saving'}
              dirty={autosave.dirty}
              error={
                autosave.status === 'failed'
                  ? `não salvo — ${autosave.error ?? 'tente de novo'}`
                  : null
              }
              onRetry={autosave.retry}
            />
          </div>
        ) : null}
      </FormBlock>

      {children}
    </div>
  )
}
